import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserCheck, UserX, Loader2 } from 'lucide-react';
import { saveStudentStatus } from '@/utils/studentStatus';
import { useToast } from '@/hooks/use-toast';

interface StudentStatusToggleProps {
  studentId: string;
  currentStatus?: boolean | null;
  onStatusChange?: (newStatus: boolean) => void;
  showLabel?: boolean;
  size?: 'sm' | 'default' | 'lg';
}

const StudentStatusToggle: React.FC<StudentStatusToggleProps> = ({
  studentId,
  currentStatus,
  onStatusChange,
  showLabel = true,
  size = 'sm'
}) => {
  useUiLanguage();
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  const handleStatusToggle = async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const newStatus = currentStatus !== true;
      const savedStatus = await saveStudentStatus(studentId, newStatus);

      toast({
        title: uiText("Success"),
        description: uiText("Student status updated to {{value0}}", { value0: newStatus ? uiText("Active") : uiText("Inactive") }),
      });

      if (onStatusChange) {
        onStatusChange(savedStatus);
      }
    } catch (error) {
      console.error('Error updating student status:', error);
      toast({
        title: uiText("Error"),
        description: error instanceof Error ? error.message : "Failed to update student status",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showLabel && (
        <Badge variant={currentStatus ? "default" : "secondary"}>
          {typeof currentStatus !== 'boolean' ? uiText("Not configured") : currentStatus ? uiText("Active") : uiText("Inactive")}
        </Badge>
      )}
      <Button
        type="button"
        aria-label={currentStatus ? uiText('Deactivate student') : uiText('Activate student')}
        onClick={handleStatusToggle}
        disabled={isUpdating}
        variant={currentStatus ? "destructive" : "default"}
        size={size}
        className="flex items-center gap-1"
      >
        {isUpdating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : currentStatus ? (
          <UserX className="h-4 w-4" />
        ) : (
          <UserCheck className="h-4 w-4" />
        )}
        {currentStatus ? uiText("Deactivate") : uiText("Activate")}
      </Button>
    </div>
  );
};

export default StudentStatusToggle;
